import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaUsers,
  FaComments,
  FaHeart,
  FaRobot,
  FaUser,
  FaCheckCircle,
  FaExclamationCircle,
  FaClock,
  FaNewspaper,
  FaDownload,
  FaFileCsv,
  FaShare,
} from "react-icons/fa";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import api from "../../services/api";

const COLORS = {
  human: "#22c55e",
  ai: "#3b82f6",
  pro: "#3b82f6",
  contra: "#f97316",
  neutral: "#6b7280",
};

const MetricCard = ({ icon, label, value, subtext, color = "blue" }) => (
  <div className={`bg-gray-700 rounded-lg p-4 border-l-4 border-${color}-400`}>
    <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {subtext && <div className="text-gray-400 text-xs mt-1">{subtext}</div>}
  </div>
);

const InstanceAnalytics = () => {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const adminToken = localStorage.getItem("admin_token");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterMode, setFilterMode] = useState("all"); // "all", "human", "ai"

  const fetchData = useCallback(async () => {
    try {
      const { data: analytics } = await api.get(
        `/admin/instances/${instanceId}/analytics`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setData(analytics);
    } catch (err) {
      console.error("Error fetching instance analytics:", err);
      setError(err.response?.data?.message || "Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  }, [adminToken, instanceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportData = async (format = "json") => {
    try {
      const response = await api.get(
        `/admin/instances/${instanceId}/export?format=${format}`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: format === "csv" ? "text" : "json",
        }
      );

      const blob = new Blob(
        [format === "csv" ? response.data : JSON.stringify(response.data, null, 2)],
        { type: format === "csv" ? "text/csv" : "application/json" }
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `spiders-instance-${instanceId}-${new Date().toISOString().split("T")[0]}.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      setError("Failed to export data");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-400 hover:text-white mb-4"
          >
            <FaArrowLeft className="mr-2" /> Back
          </button>
          <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg">
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Prepare pie chart data
  const userTypePieData = [
    { name: "Human", value: data?.users?.human || 0, color: COLORS.human },
    { name: "AI", value: data?.users?.ai || 0, color: COLORS.ai },
  ];

  const messageTypePieData = [
    { name: "By Humans", value: data?.content?.messages?.byHuman || 0, color: COLORS.human },
    { name: "By AI", value: data?.content?.messages?.byAI || 0, color: COLORS.ai },
  ];

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-400 hover:text-white mr-4"
            >
              <FaArrowLeft className="mr-2" /> Back
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                Instance: {data?.instance?.participantId || instanceId}
              </h1>
              <p className="text-gray-400 text-sm">
                Condition: {data?.instance?.conditionName || data?.instance?.conditionId}
                {data?.instance?.isTestInstance && (
                  <span className="ml-2 px-2 py-0.5 bg-yellow-900 text-yellow-200 rounded text-xs">
                    TEST
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Human/AI Filter Toggle */}
            <div className="flex rounded-lg border border-gray-600 overflow-hidden">
              {[
                { key: "all", label: "All" },
                { key: "human", label: "Human", icon: <FaUser className="w-3 h-3" /> },
                { key: "ai", label: "AI", icon: <FaRobot className="w-3 h-3" /> },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setFilterMode(opt.key)}
                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
                    filterMode === opt.key
                      ? "bg-blue-600 text-white"
                      : "bg-gray-700 text-gray-400 hover:text-white"
                  }`}
                >
                  {opt.icon} {opt.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => exportData("json")}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
              >
                <FaDownload /> JSON
              </button>
              <button
                onClick={() => exportData("csv")}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
              >
                <FaFileCsv /> CSV
              </button>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm ${
                data?.instance?.status === "active"
                  ? "bg-green-900 text-green-200"
                  : data?.instance?.status === "completed"
                  ? "bg-blue-900 text-blue-200"
                  : data?.instance?.status === "error"
                  ? "bg-red-900 text-red-200"
                  : "bg-gray-700 text-gray-300"
              }`}
            >
              {data?.instance?.status?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <MetricCard
            icon={<FaUsers />}
            label="Total Users"
            value={data?.users?.total || 0}
            subtext={`${data?.users?.human || 0} human, ${data?.users?.ai || 0} AI`}
            color="blue"
          />
          <MetricCard
            icon={<FaComments />}
            label="Messages"
            value={data?.content?.messages?.total || 0}
            subtext={`${data?.content?.messages?.byHuman || 0} by humans`}
            color="green"
          />
          <MetricCard
            icon={<FaComments />}
            label="Comments"
            value={data?.content?.comments?.total || 0}
            subtext={`${data?.content?.comments?.byHuman || 0} by humans`}
            color="purple"
          />
          <MetricCard
            icon={<FaHeart />}
            label="Interactions"
            value={data?.interactions?.total || 0}
            subtext={`${data?.interactions?.byHuman || 0} by humans`}
            color="red"
          />
          <MetricCard
            icon={<FaNewspaper />}
            label="Feed Views"
            value={data?.feedExposure?.totalImpressions || 0}
            subtext="by human users"
            color="cyan"
          />
          <MetricCard
            icon={<FaClock />}
            label="Created"
            value={new Date(data?.instance?.createdAt).toLocaleDateString()}
            subtext={new Date(data?.instance?.createdAt).toLocaleTimeString()}
            color="gray"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* User Distribution */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">User Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={userTypePieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {userTypePieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Message Distribution */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Message Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={messageTypePieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {messageTypePieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Feed Exposure */}
          {data?.feedExposure && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Feed Exposure</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Avg messages/view</span>
                  <span className="text-white font-semibold">
                    {data.feedExposure.avgMessagesPerView?.toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Pro stance %</span>
                  <span className="text-blue-400 font-semibold">
                    {((data.feedExposure.proStanceRatio || 0) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">AI content %</span>
                  <span className="text-cyan-400 font-semibold">
                    {(
                      (data.feedExposure.avgArtificialContent /
                        (data.feedExposure.avgArtificialContent + data.feedExposure.avgHumanContent || 1)) *
                      100
                    ).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Interaction Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Interactions by Type</h3>
            <div className="space-y-3">
              {data?.interactions?.byType &&
                Object.entries(data.interactions.byType).map(([type, counts]) => (
                  <div key={type} className="bg-gray-700 rounded p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-medium capitalize">{type}s</span>
                      <span className="text-gray-400">
                        Total: {(counts.byHuman || 0) + (counts.byAI || 0)}
                      </span>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <FaUser className="text-green-400" />
                        <span className="text-green-400">{counts.byHuman || 0}</span>
                        <span className="text-gray-500">by humans</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FaRobot className="text-blue-400" />
                        <span className="text-blue-400">{counts.byAI || 0}</span>
                        <span className="text-gray-500">by AI</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Content Reception Matrix */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Content Reception</h3>
            {data?.contentReception ? (
              <div className="space-y-4">
                <div className="bg-gray-700 rounded p-3">
                  <h4 className="text-gray-300 text-sm font-medium mb-2">Likes (actor → target)</h4>
                  <div className="grid grid-cols-3 gap-1 text-xs text-center">
                    <div></div>
                    <div className="text-green-400 font-medium">Human posts</div>
                    <div className="text-blue-400 font-medium">AI posts</div>
                    <div className="text-green-400 font-medium text-right pr-2">Human</div>
                    <div className="bg-green-900/40 rounded p-2 text-white font-bold">{data.contentReception.humanLikesOnHumanContent}</div>
                    <div className="bg-blue-900/40 rounded p-2 text-white font-bold">{data.contentReception.humanLikesOnAIContent}</div>
                    <div className="text-blue-400 font-medium text-right pr-2">AI</div>
                    <div className="bg-green-900/20 rounded p-2 text-gray-300">{data.contentReception.aiLikesOnHumanContent}</div>
                    <div className="bg-blue-900/20 rounded p-2 text-gray-300">{data.contentReception.aiLikesOnAIContent}</div>
                  </div>
                </div>
                <div className="bg-gray-700 rounded p-3">
                  <h4 className="text-gray-300 text-sm font-medium mb-2">Comments (actor → target)</h4>
                  <div className="grid grid-cols-3 gap-1 text-xs text-center">
                    <div></div>
                    <div className="text-green-400 font-medium">Human posts</div>
                    <div className="text-blue-400 font-medium">AI posts</div>
                    <div className="text-green-400 font-medium text-right pr-2">Human</div>
                    <div className="bg-green-900/40 rounded p-2 text-white font-bold">{data.contentReception.humanCommentsOnHumanContent}</div>
                    <div className="bg-blue-900/40 rounded p-2 text-white font-bold">{data.contentReception.humanCommentsOnAIContent}</div>
                    <div className="text-blue-400 font-medium text-right pr-2">AI</div>
                    <div className="bg-green-900/20 rounded p-2 text-gray-300">{data.contentReception.aiCommentsOnHumanContent}</div>
                    <div className="bg-blue-900/20 rounded p-2 text-gray-300">{data.contentReception.aiCommentsOnAIContent}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-sm">No interaction data yet</div>
            )}
          </div>
        </div>

        {/* Dwell Time & Engagement Funnel Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Dwell Time by Stance */}
          {data?.dwellTime && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-2">Dwell Time by Stance</h3>
              <p className="text-gray-400 text-xs mb-4">Average time spent reading posts by author stance</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[
                  { stance: "Pro", avgMs: data.dwellTime.byMessageStance?.pro?.avgDwellMs || 0, count: data.dwellTime.byMessageStance?.pro?.messageCount || 0 },
                  { stance: "Contra", avgMs: data.dwellTime.byMessageStance?.contra?.avgDwellMs || 0, count: data.dwellTime.byMessageStance?.contra?.messageCount || 0 },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="stance" stroke="#9CA3AF" tick={{ fill: "#9CA3AF" }} />
                  <YAxis stroke="#9CA3AF" tick={{ fill: "#9CA3AF" }} tickFormatter={v => `${(v / 1000).toFixed(1)}s`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1F2937", border: "1px solid #374151" }}
                    labelStyle={{ color: "#fff" }}
                    formatter={(v) => [`${(v / 1000).toFixed(1)}s`, "Avg dwell"]}
                  />
                  <Bar dataKey="avgMs" fill={COLORS.pro} radius={[4, 4, 0, 0]}>
                    <Cell fill={COLORS.pro} />
                    <Cell fill={COLORS.contra} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>Avg scroll depth: {data.dwellTime.avgScrollDepthPercent?.toFixed(0) || 0}%</span>
                <span>Total dwell: {(data.dwellTime.totalDwellMs / 1000).toFixed(0)}s</span>
              </div>
            </div>
          )}

          {/* Engagement Funnel */}
          {data?.engagementFunnel && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-2">Engagement Funnel</h3>
              <p className="text-gray-400 text-xs mb-4">Posts: served in feed → viewed (scrolled past) → engaged (liked/commented)</p>
              <div className="space-y-3">
                {[
                  { label: "Exposed (in feed)", value: data.engagementFunnel.exposed, color: "bg-cyan-500" },
                  { label: "Viewed (scrolled)", value: data.engagementFunnel.viewed, color: "bg-blue-500" },
                  { label: "Engaged (liked/commented)", value: data.engagementFunnel.engaged, color: "bg-green-500" },
                ].map((step, i) => {
                  const maxVal = data.engagementFunnel.exposed || 1;
                  const pct = maxVal > 0 ? (step.value / maxVal * 100) : 0;
                  return (
                    <div key={step.label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-300">{step.label}</span>
                        <span className="text-white font-medium">{step.value} <span className="text-gray-500 text-xs">({pct.toFixed(0)}%)</span></span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-4">
                        <div
                          className={`${step.color} h-4 rounded-full transition-all`}
                          style={{ width: `${Math.max(pct, 2)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Human Participants Table */}
        {data?.humanParticipants && data.humanParticipants.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Human Participants ({data.humanParticipants.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-gray-700 text-gray-300">
                    <th className="p-3 rounded-tl-lg">Username</th>
                    <th className="p-3 text-center">Messages</th>
                    <th className="p-3 text-center">Comments</th>
                    <th className="p-3 text-center">Likes</th>
                    <th className="p-3 text-center">Follows</th>
                    <th className="p-3 text-center">Feed Views</th>
                    <th className="p-3 rounded-tr-lg">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.humanParticipants.map((user, idx) => (
                    <tr
                      key={user.id}
                      className={idx % 2 === 0 ? "bg-gray-800" : "bg-gray-750"}
                    >
                      <td className="p-3 text-white font-medium">{user.username}</td>
                      <td className="p-3 text-center text-yellow-400">{user.messages}</td>
                      <td className="p-3 text-center text-purple-400">{user.comments}</td>
                      <td className="p-3 text-center text-red-400">{user.likes}</td>
                      <td className="p-3 text-center text-blue-400">{user.follows}</td>
                      <td className="p-3 text-center text-cyan-400">{user.feedViews}</td>
                      <td className="p-3 text-gray-400 text-xs">
                        {new Date(user.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Activity Timeline */}
        {data?.activityTimeline && data.activityTimeline.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Activity Timeline</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.activityTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="time"
                  stroke="#9CA3AF"
                  tick={{ fill: "#9CA3AF", fontSize: 10 }}
                  tickFormatter={(v) => v.split("T")[1]?.slice(0, 5) || v}
                />
                <YAxis stroke="#9CA3AF" tick={{ fill: "#9CA3AF" }} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#1F2937", border: "1px solid #374151" }}
                  labelStyle={{ color: "#fff" }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="humanMessages"
                  stroke={COLORS.human}
                  name="Human Messages"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="aiMessages"
                  stroke={COLORS.ai}
                  name="AI Messages"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="comments"
                  stroke="#a855f7"
                  name="Comments"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstanceAnalytics;
